'use client'
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  Grid2,
  Paper,
  Card,
  CardContent,
  Container,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import {
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from "@mui/icons-material";

import UserInfo from "app/Pages/_sharedSections/PublicUserInfo";
import MsgBtn from "app/Components/Buttons/FilledBtns/MsgBtn";
import WhitelistBtn from "app/Components/Buttons/FilledBtns/HostBtns/WhitelistBtn";
import BlacklistBtn from "app/Components/Buttons/FilledBtns/HostBtns/BlacklistBtn";
import AddNoteBtn from "app/Components/Buttons/FilledBtns/AddNoteBtn";

import placeholder from "images/profile-placeholder.avif";

import NotesModal from "app/Components/Modals/NotesModal.jsx";

import { db, storage } from "config/firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { doc, getDoc } from "firebase/firestore";
import ProfileModal from "./ProfileModal.jsx";

function PublicProfile() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const claims = useSelector((state: any) => state.user.token);
  const whitelist = useSelector((state: any) => state.host.whitelist);
  const blacklist = useSelector((state: any) => state.host.blacklist);
  const [modalDets, setModalDets] = useState<any>({ open: false });
  const [notesModalDets, setNotesModalDets] = useState<any>({
    open: false,
    itemId: "",
    itemName: "",
  });
  const [reqInfo, setReqInfo] = useState<any>({});
  const [userData, setUserData] = useState<any>({
    joinDate: "",
    profileBio: "",
  });

  const params = useParams<{ userId: string }>();
  const [imgURL, setImgURL] = useState(placeholder);

  useEffect(() => {
    getProfilePicture();
    getUserData();
  }, []);

  useEffect(() => {
    console.log(`userData`, userData);
  }, [userData]);

  function getProfilePicture() {
    if (!params.userId) return;
    const profilePictureRef = ref(storage, `profilePictures/${params.userId}`);
    getDownloadURL(profilePictureRef)
      .then((url) => {
        setImgURL(url);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const getUserData = async () => {
    if (!params.userId) return;
    const userRef = doc(db, "users", params.userId);
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
      setUserData({
        ...docSnap.data(),
      });
    }
  };

  const onClickWhitelist = () => {
    if (blacklist[userData.id]) {
      setModalDets({ open: true, status: "error" });
    } else {
      setModalDets({ open: true });
    }
    setReqInfo({
      playerName: userData.displayName,
      playerId: userData.id,
      listType: "Whitelist",
    });
  };

  const onClickBlacklist = () => {
    if (whitelist[userData.id]) {
      setModalDets({ open: true, status: "error" });
    } else {
      setModalDets({ open: true });
    }
    setReqInfo({
      playerName: userData.displayName,
      playerId: userData.id,
      listType: "Blacklist",
    });
  };

  const renderCommonButtons = () => (
    <>
      <MsgBtn userId={userData.id} receiverName={userData.displayName} />
      <AddNoteBtn
        action={() => {
          setNotesModalDets({
            open: true,
            itemId: userData.id,
            itemName: userData.displayName,
          });
        }}
      />
    </>
  );

  const renderStatusBadge = () => {
    if (!claims.isHost) return null;
    
    const isWhitelisted = whitelist[userData.id];
    const isBlacklisted = blacklist[userData.id];
    
    if (isWhitelisted) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Whitelisted"
          color="success"
          variant="filled"
          sx={{ mb: 2 }}
        />
      );
    }
    
    if (isBlacklisted) {
      return (
        <Chip
          icon={<BlockIcon />}
          label="Blacklisted"
          color="error"
          variant="filled"
          sx={{ mb: 2 }}
        />
      );
    }
    
    return null;
  };

  const renderHostButtons = () => {
    if (claims.isHost) {
      return (
        <>
          <WhitelistBtn
            action={onClickWhitelist}
            addToList={!whitelist[userData.id]}
          />
          <BlacklistBtn
            action={onClickBlacklist}
            addToList={!blacklist[userData.id]}
          />
        </>
      );
    }
  };

  if (!userData || !claims) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "primary.main",
            fontWeight: 600,
          }}
        >
          <PersonIcon
            color="primary"
            fontSize={isMobile ? "large" : "inherit"}
          />
          {userData.displayName}'s Profile
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ maxWidth: 600, mx: "auto" }}
        >
          View player information and manage your interactions
        </Typography>
      </Box>

      {/* Profile Content */}
      <Grid2 container spacing={4} sx={{ justifyContent: "center" }}>
        {/* Avatar and Actions */}
        <Grid2 xs={12} md={6}>
          <Card elevation={2} sx={{ borderRadius: 2, textAlign: "center" }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 3 }}>
                <Avatar
                  alt="Profile Picture"
                  src={imgURL}
                  sx={{
                    width: { xs: 200, md: 300 },
                    height: { xs: 240, md: 350 },
                    mx: "auto",
                    mb: 3,
                    border: `4px solid ${theme.palette.primary.light}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <AccountCircleIcon sx={{ fontSize: { xs: 100, md: 150 } }} />
                </Avatar>
                
                {/* Status Badge */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {renderStatusBadge()}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {renderCommonButtons()}
                {renderHostButtons()}
              </Box>
            </CardContent>
          </Card>
        </Grid2>

        {/* User Information */}
        <Grid2 xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              borderRadius: 2,
              bgcolor: "background.paper",
              height: "fit-content",
            }}
          >
            <UserInfo userData={userData} />
          </Paper>
        </Grid2>
      </Grid2>

      {/* Modals */}
      <NotesModal dets={notesModalDets} />
      <ProfileModal dets={modalDets} reqInfo={reqInfo} />
    </Container>
  );
}

export default PublicProfile;
